import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { CspConfig, getCspConfigId } from 'data/cspConfig/cspConfig.types';
import * as cspConfigSelectors from 'data/cspConfig/cspConfig.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/cspConfig/cspConfig.actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import { FormTemplateSection } from 'data/template/template.types';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import CspConfigForm, { CspConfigFormRef } from '../../components/CspConfigForm';
import { cspConfigTemplate } from '../../templates/cspConfigTemplate';
import getContactOptions from '../../utils/getContactOptions';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedCspConfig: cspConfigSelectors.getSelectedCspConfig(state),
  cspConfig: cspConfigSelectors.getCspConfig(state),
  contacts: contactSelectors.getContacts(state),
});

const mapDispatch = {
  goToCspConfigEdit: actions.goToCspConfigEdit,
  getCspConfig: actions.getCspConfig,
  goToCspConfigList: actions.goToCspConfigList,
  getContacts: contactActions.getAllContacts,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

const readOnlyFields = [
  'name',
  'cspid',
  'legalcontact',
  'nwcontact',
];

export const CspConfigView: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedCspConfig,
  history,
  cspConfig,
  contacts,
  getCspConfig,
  goToCspConfigEdit,
  goToCspConfigList,
  getContacts,
}: Props) => {
  const [template, setTemplate] = useState(cspConfigTemplate);
  const [formData, setFormData] = useState<Partial<CspConfig>>({});
  const cspConfigFormRef = useRef<CspConfigFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getContacts(deliveryFunctionId);
  }, [deliveryFunctionId, getContacts]);

  useEffect(() => {
    const id = getCspConfigId(selectedCspConfig[0]);
    if (id) {
      getCspConfig(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, selectedCspConfig, getCspConfig]);

  useEffect(() => {
    if (cspConfig != null) {
      setFormData(cspConfig);

      const clonedTemplate = cloneDeep(cspConfigTemplate);
      clonedTemplate.formId = Date.now().toString();

      readOnlyFields.forEach((fieldName) => {
        (clonedTemplate.main as FormTemplateSection).fields[fieldName].readOnly = true;
        (clonedTemplate.main as FormTemplateSection).fields[fieldName].disabled = true;
      });

      (clonedTemplate.main as FormTemplateSection).fields.legalcontact.options =
        getContactOptions(contacts, cspConfig.legalcontact);
      (clonedTemplate.main as FormTemplateSection).fields.nwcontact.options =
        getContactOptions(contacts, cspConfig.nwcontact);

      setTemplate(clonedTemplate);
    }
  }, [cspConfig, contacts]);

  const handleClickCancel = () => goToCspConfigList(history);

  const handleClickEdit = () => goToCspConfigEdit(history);

  if (!cspConfig) {
    return null;
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleClickCancel}
          onClickClose={handleClickCancel}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="CspConfigView" className="read-only">
      {renderFormPanel(t('cspConfig.view.title'))}
      <CspConfigForm
        data={formData}
        template={template}
        ref={cspConfigFormRef}
      />
    </div>
  );
};

export default withRouter(connector(CspConfigView));
